'use client'

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
    Bold,
    Italic,
    Heading3,
    Pilcrow,
    Beaker,
    Save,
    ArrowLeft,
    Globe,
    Mail,
    Eye,
    Sparkles,
    Code2,
    Settings2,
    Pencil,
    Smartphone,
    Monitor,
    CheckCircle2,
    RotateCcw,
    ShoppingCart,
    Tag,
    CalendarClock,
    Repeat,
    XCircle,
    AlertTriangle,
    Timer,
    UserMinus,
    ChevronRight,
    type LucideIcon,
} from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { templateRegistry, getDefaultTemplate, TemplateType } from '../../../../../../components/email/template/templateRegistry'
import type { EmailTemplateItem } from '@/lib/api/emailTemplates'
import {
    EmailBuilder,
    ImagePickerProvider,
    PreviewFrame,
    StarterGallery,
    buildMockVariableValues,
    renderEmailDocument,
    STARTERS_BY_EMAIL_TYPE,
    DEFAULT_STARTERS,
    type EmailDocument,
    type EmailStarterTemplate,
    type EmailVariable,
} from '@/components/email-builder'

/**
 * Supported languages for email templates. Any 2-letter ISO-639 code can be
 * used in the data model — this map just controls which ones are discoverable
 * in the language switcher by default. Adding a new language here is enough
 * to expose it everywhere (flag + native name + English label).
 */
interface LocaleInfo {
    /** Native name */
    name: string
    /** English name, for search */
    english: string
    /** Flag emoji */
    flag: string
}

const ALL_LOCALES: Record<string, LocaleInfo> = {
    en: { name: 'English', english: 'English', flag: '\uD83C\uDDEC\uD83C\uDDE7' },
    fr: { name: 'Fran\u00e7ais', english: 'French', flag: '\uD83C\uDDEB\uD83C\uDDF7' },
    es: { name: 'Espa\u00f1ol', english: 'Spanish', flag: '\uD83C\uDDEA\uD83C\uDDF8' },
    de: { name: 'Deutsch', english: 'German', flag: '\uD83C\uDDE9\uD83C\uDDEA' },
    it: { name: 'Italiano', english: 'Italian', flag: '\uD83C\uDDEE\uD83C\uDDF9' },
    pt: { name: 'Portugu\u00eas', english: 'Portuguese', flag: '\uD83C\uDDF5\uD83C\uDDF9' },
    nl: { name: 'Nederlands', english: 'Dutch', flag: '\uD83C\uDDF3\uD83C\uDDF1' },
    pl: { name: 'Polski', english: 'Polish', flag: '\uD83C\uDDF5\uD83C\uDDF1' },
    sv: { name: 'Svenska', english: 'Swedish', flag: '\uD83C\uDDF8\uD83C\uDDEA' },
    no: { name: 'Norsk', english: 'Norwegian', flag: '\uD83C\uDDF3\uD83C\uDDF4' },
    da: { name: 'Dansk', english: 'Danish', flag: '\uD83C\uDDE9\uD83C\uDDF0' },
    fi: { name: 'Suomi', english: 'Finnish', flag: '\uD83C\uDDEB\uD83C\uDDEE' },
    cs: { name: '\u010ce\u0161tina', english: 'Czech', flag: '\uD83C\uDDE8\uD83C\uDDFF' },
    sk: { name: 'Sloven\u010dina', english: 'Slovak', flag: '\uD83C\uDDF8\uD83C\uDDF0' },
    hu: { name: 'Magyar', english: 'Hungarian', flag: '\uD83C\uDDED\uD83C\uDDFA' },
    ro: { name: 'Rom\u00e2n\u0103', english: 'Romanian', flag: '\uD83C\uDDF7\uD83C\uDDF4' },
    bg: { name: '\u0411\u044a\u043b\u0433\u0430\u0440\u0441\u043a\u0438', english: 'Bulgarian', flag: '\uD83C\uDDE7\uD83C\uDDEC' },
    el: { name: '\u0395\u03bb\u03bb\u03b7\u03bd\u03b9\u03ba\u03ac', english: 'Greek', flag: '\uD83C\uDDEC\uD83C\uDDF7' },
    tr: { name: 'T\u00fcrk\u00e7e', english: 'Turkish', flag: '\uD83C\uDDF9\uD83C\uDDF7' },
    ru: { name: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439', english: 'Russian', flag: '\uD83C\uDDF7\uD83C\uDDFA' },
    uk: { name: '\u0423\u043a\u0440\u0430\u0457\u043d\u0441\u044c\u043a\u0430', english: 'Ukrainian', flag: '\uD83C\uDDFA\uD83C\uDDE6' },
    ja: { name: '\u65e5\u672c\u8a9e', english: 'Japanese', flag: '\uD83C\uDDEF\uD83C\uDDF5' },
    ko: { name: '\ud55c\uad6d\uc5b4', english: 'Korean', flag: '\uD83C\uDDF0\uD83C\uDDF7' },
    zh: { name: '\u4e2d\u6587', english: 'Chinese', flag: '\uD83C\uDDE8\uD83C\uDDF3' },
    ar: { name: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', english: 'Arabic', flag: '\uD83C\uDDF8\uD83C\uDDE6' },
    he: { name: '\u05e2\u05d1\u05e8\u05d9\u05ea', english: 'Hebrew', flag: '\uD83C\uDDEE\uD83C\uDDF1' },
    hi: { name: '\u0939\u093f\u0928\u094d\u0926\u0940', english: 'Hindi', flag: '\uD83C\uDDEE\uD83C\uDDF3' },
    th: { name: '\u0e44\u0e17\u0e22', english: 'Thai', flag: '\uD83C\uDDF9\uD83C\uDDED' },
    vi: { name: 'Ti\u1ebfng Vi\u1ec7t', english: 'Vietnamese', flag: '\uD83C\uDDFB\uD83C\uDDF3' },
    id: { name: 'Bahasa Indonesia', english: 'Indonesian', flag: '\uD83C\uDDEE\uD83C\uDDE9' },
    ms: { name: 'Bahasa Melayu', english: 'Malay', flag: '\uD83C\uDDF2\uD83C\uDDFE' },
}

type LocaleCode = keyof typeof ALL_LOCALES

/**
 * Curated default set shown in the switcher before the merchant activates
 * more languages. We lean on the most common EU shop languages.
 */
const DEFAULT_VISIBLE_LOCALES: LocaleCode[] = ['en', 'fr', 'es', 'de', 'it', 'pt']

/**
 * Legacy compat: some parts of the UI still reference `LOCALES`/`LOCALE_FLAGS`
 * as plain maps — we expose the same shape derived from `ALL_LOCALES`.
 */
const LOCALES = Object.fromEntries(
    Object.entries(ALL_LOCALES).map(([k, v]) => [k, v.name]),
) as Record<LocaleCode, string>

const LOCALE_FLAGS = Object.fromEntries(
    Object.entries(ALL_LOCALES).map(([k, v]) => [k, v.flag]),
) as Record<LocaleCode, string>

const LOCALE_CODES = Object.fromEntries(
    Object.keys(ALL_LOCALES).map((k) => [k, k.toUpperCase()]),
) as Record<LocaleCode, string>

enum AVAILABLE_TAG {
    P = 'p',
    STRONG = 'strong',
    EM = 'em',
    H3 = 'h3',
}

const TAG_CONFIG: { tag: AVAILABLE_TAG; icon: LucideIcon; label: string }[] = [
    { tag: AVAILABLE_TAG.STRONG, icon: Bold, label: 'Bold' },
    { tag: AVAILABLE_TAG.EM, icon: Italic, label: 'Italic' },
    { tag: AVAILABLE_TAG.H3, icon: Heading3, label: 'Heading' },
    { tag: AVAILABLE_TAG.P, icon: Pilcrow, label: 'Paragraph' },
]

const EMAIL_TYPES = Object.entries(templateRegistry).map(([value, config]) => ({
    value,
    label: config.label,
    description: (config as any).description || '',
}))

/**
 * Visual metadata for each email template type. Keeping this colocated with
 * the editor (rather than inside `templateRegistry`) lets us pick icons and
 * accent colors without reaching across app boundaries.
 */
const EMAIL_TYPE_META: Record<
    string,
    { icon: LucideIcon; accent: string; group: string }
> = {
    order_paid: { icon: CheckCircle2, accent: '#059669', group: 'Orders' },
    order_pending: { icon: Timer, accent: '#d97706', group: 'Orders' },
    order_refunded: { icon: RotateCcw, accent: '#0ea5e9', group: 'Orders' },
    abandoned_checkout: { icon: ShoppingCart, accent: '#f59e0b', group: 'Orders' },
    customer_promotion_code: { icon: Tag, accent: '#ec4899', group: 'Marketing' },
    subscription_rebill_upcoming: { icon: CalendarClock, accent: '#0284c7', group: 'Subscriptions' },
    subscription_rebill_succeeded: { icon: Repeat, accent: '#10b981', group: 'Subscriptions' },
    subscription_canceled: { icon: XCircle, accent: '#ef4444', group: 'Subscriptions' },
    subscription_past_due: { icon: AlertTriangle, accent: '#f97316', group: 'Subscriptions' },
    free_trial_expiration_incoming: { icon: Timer, accent: '#8b5cf6', group: 'Subscriptions' },
    club_membership_started: { icon: Sparkles, accent: '#6366f1', group: 'Membership' },
    club_membership_deactivated: { icon: UserMinus, accent: '#6b7280', group: 'Membership' },
}

const GROUP_ORDER = ['Orders', 'Subscriptions', 'Membership', 'Marketing']

interface EmailTemplateEditorProps {
    template: EmailTemplateItem
    onSave: (data: Partial<EmailTemplateItem>) => void
    onTest?: (email: string) => Promise<void>
    isLoading?: boolean
    title: string
    subtitle?: string
    backTo: string
}

const getAvailableVariables = (templateType: TemplateType) => {
    return templateRegistry[templateType]?.variables || []
}

function VariableChip({ variable, onClick }: { variable: { key: string; label: string; description?: string }; onClick: () => void }) {
    const chip = (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-muted-foreground/30 bg-muted/50 px-2 py-0.5 text-xs font-mono text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
        >
            <span className="opacity-50">{'{'}</span>
            {variable.label}
            <span className="opacity-50">{'}'}</span>
        </button>
    )

    if (variable.description) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>{chip}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                    <p className="font-mono text-[10px] opacity-70">{'{{ ' + variable.key + ' }}'}</p>
                    <p>{variable.description}</p>
                </TooltipContent>
            </Tooltip>
        )
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{chip}</TooltipTrigger>
            <TooltipContent side="top">
                <p className="font-mono text-[10px]">{'{{ ' + variable.key + ' }}'}</p>
            </TooltipContent>
        </Tooltip>
    )
}

/**
 * Per-locale completion status, used to render the indicator dots in the
 * locale switcher ("translated" / "subject only" / "empty").
 */
type LocaleStatus = 'full' | 'partial' | 'empty'

function LocaleSwitcher({
    currentLocale,
    onLocaleChange,
    onAddLocale,
    statusByLocale,
    availableLocales,
    variant = 'solid',
    align = 'end',
}: {
    currentLocale: LocaleCode
    onLocaleChange: (locale: LocaleCode) => void
    /** Called when the merchant picks a locale that isn't active yet */
    onAddLocale?: (locale: LocaleCode) => void
    /** Map of locale → completion status, drives the dot indicator */
    statusByLocale?: Partial<Record<LocaleCode, LocaleStatus>>
    /** Locales displayed in the primary list. Anything not in here is reachable
     *  through the "Add a language" picker. */
    availableLocales?: LocaleCode[]
    /** `solid` = emphasized button, `ghost` = inline/subtle */
    variant?: 'solid' | 'ghost'
    align?: 'start' | 'center' | 'end'
}) {
    const { t } = useTranslation(['emails'])
    const [open, setOpen] = useState(false)
    const [mode, setMode] = useState<'list' | 'add'>('list')
    const [query, setQuery] = useState('')

    const locales = availableLocales || DEFAULT_VISIBLE_LOCALES

    const handleSelect = (code: LocaleCode) => {
        onLocaleChange(code)
        setOpen(false)
        setMode('list')
    }

    const handleAdd = (code: LocaleCode) => {
        if (onAddLocale) onAddLocale(code)
        onLocaleChange(code)
        setOpen(false)
        setMode('list')
        setQuery('')
    }

    // Remaining locales (not already listed)
    const remaining = (Object.keys(ALL_LOCALES) as LocaleCode[]).filter(
        (code) => !locales.includes(code),
    )
    const filteredRemaining = remaining.filter((code) => {
        if (!query.trim()) return true
        const q = query.trim().toLowerCase()
        const info = ALL_LOCALES[code]
        return (
            code.toLowerCase().includes(q) ||
            info.name.toLowerCase().includes(q) ||
            info.english.toLowerCase().includes(q)
        )
    })

    const currentInfo = ALL_LOCALES[currentLocale]

    return (
        <Popover
            open={open}
            onOpenChange={(v) => {
                setOpen(v)
                if (!v) {
                    setMode('list')
                    setQuery('')
                }
            }}
        >
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
                        variant === 'solid'
                            ? 'border bg-background hover:bg-muted'
                            : 'hover:bg-muted',
                    )}
                    aria-label={t('editor.localeSwitcher.currentAria', {
                        name: currentInfo?.name ?? currentLocale,
                    })}
                >
                    <span className="text-sm leading-none">
                        {currentInfo?.flag ?? '\uD83C\uDF10'}
                    </span>
                    <span className="tracking-wide">
                        {currentLocale.toUpperCase()}
                    </span>
                    <svg
                        aria-hidden
                        viewBox="0 0 20 20"
                        className="h-3 w-3 text-muted-foreground"
                        fill="currentColor"
                    >
                        <path d="M5.5 7.5l4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align={align}
                sideOffset={6}
                className="w-64 p-0"
            >
                {mode === 'list' && (
                    <>
                        <div className="flex items-center justify-between px-2 py-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('editor.localeSwitcher.language')}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                                {t('editor.localeSwitcher.active', { count: locales.length })}
                            </span>
                        </div>
                        <div className="max-h-72 overflow-y-auto px-1 pb-1">
                            {locales.map((code) => {
                                const isActive = currentLocale === code
                                const status = statusByLocale?.[code] ?? 'empty'
                                const info = ALL_LOCALES[code]
                                if (!info) return null
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        onClick={() => handleSelect(code)}
                                        className={cn(
                                            'flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm transition-colors',
                                            isActive
                                                ? 'bg-primary/10 text-foreground'
                                                : 'text-foreground hover:bg-muted',
                                        )}
                                    >
                                        <span className="text-base leading-none">
                                            {info.flag}
                                        </span>
                                        <div className="min-w-0 flex-1 text-left">
                                            <div className="truncate text-xs font-medium">
                                                {info.name}
                                            </div>
                                            <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                                                {code}
                                            </div>
                                        </div>
                                        <LocaleStatusDot status={status} />
                                        {isActive && (
                                            <span
                                                aria-hidden
                                                className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                                            />
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                        <div className="border-t p-1">
                            <button
                                type="button"
                                onClick={() => setMode('add')}
                                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                            >
                                <svg
                                    aria-hidden
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    className="h-3.5 w-3.5 text-muted-foreground"
                                >
                                    <path d="M10 4v12M4 10h12" strokeLinecap="round" />
                                </svg>
                                {t('editor.localeSwitcher.addLanguage')}
                            </button>
                        </div>
                        <div className="border-t px-2 py-1.5 text-[10px] text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                                <LocaleStatusDot status="full" />
                                <span>{t('editor.localeSwitcher.statusFull')}</span>
                                <span className="opacity-40">·</span>
                                <LocaleStatusDot status="partial" />
                                <span>{t('editor.localeSwitcher.statusPartial')}</span>
                                <span className="opacity-40">·</span>
                                <LocaleStatusDot status="empty" />
                                <span>{t('editor.localeSwitcher.statusEmpty')}</span>
                            </div>
                        </div>
                    </>
                )}

                {mode === 'add' && (
                    <>
                        <div className="flex items-center gap-2 border-b p-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setMode('list')
                                    setQuery('')
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                                aria-label={t('editor.localeSwitcher.back')}
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                            </button>
                            <Input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t('editor.localeSwitcher.searchLanguage')}
                                className="h-7 text-xs"
                            />
                        </div>
                        <div className="max-h-72 overflow-y-auto p-1">
                            {filteredRemaining.length === 0 && (
                                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                                    {t('editor.localeSwitcher.noLanguageMatches', { query })}
                                </div>
                            )}
                            {filteredRemaining.map((code) => {
                                const info = ALL_LOCALES[code]
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        onClick={() => handleAdd(code)}
                                        className="flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-muted"
                                    >
                                        <span className="text-base leading-none">
                                            {info.flag}
                                        </span>
                                        <div className="min-w-0 flex-1 text-left">
                                            <div className="truncate text-xs font-medium">
                                                {info.name}
                                            </div>
                                            <div className="truncate text-[10px] text-muted-foreground">
                                                {info.english} · {code.toUpperCase()}
                                            </div>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    )
}

function LocaleStatusDot({ status }: { status: LocaleStatus }) {
    const cls =
        status === 'full'
            ? 'bg-emerald-500'
            : status === 'partial'
              ? 'bg-amber-400'
              : 'bg-muted-foreground/30'
    return (
        <span
            aria-hidden
            className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', cls)}
        />
    )
}


export function EmailTemplateEditor({ template, onSave, onTest, isLoading, title, subtitle, backTo }: EmailTemplateEditorProps) {
    const { t } = useTranslation(['emails', 'common'])
    const [currentLocale, setCurrentLocale] = useState<LocaleCode>('en')
    /**
     * Locales the merchant has explicitly enabled in this session (beyond the
     * default set). Any locale that already has saved content will surface in
     * the switcher automatically via `visibleLocales` below.
     */
    const [extraLocales, setExtraLocales] = useState<LocaleCode[]>([])
    const [data, setData] = useState<Partial<EmailTemplateItem>>(() => {
        if (!template.id) {
            return {
                name: '',
                type: undefined,
                subject: { en: '' },
                active: true,
                params: { replyTo: undefined },
                templateVariables: {},
            }
        }
        return {
            name: template.name,
            type: template.type,
            subject: template.subject || { en: '' },
            active: template.active ?? true,
            params: template.params || { replyTo: undefined },
            templateVariables: template.templateVariables || {},
        }
    })

    const [testEmail, setTestEmail] = useState('')
    const [isTestDialogOpen, setIsTestDialogOpen] = useState(false)
    const [isTesting, setIsTesting] = useState(false)

    const subjectRef = useRef<HTMLTextAreaElement>(null)
    const bodyRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        if (template.id) {
            setData({
                name: template.name,
                type: template.type,
                subject: template.subject || { en: '' },
                active: template.active ?? true,
                params: template.params || { replyTo: undefined },
                templateVariables: template.templateVariables || {},
            })
        }
    }, [template])

    const handleSubmit = useCallback(
        (e?: React.FormEvent) => {
            e?.preventDefault()
            onSave(data)
        },
        [data, onSave],
    )

    const handleTypeChange = useCallback(
        (newType: TemplateType) => {
            if (newType !== data.type) {
                const defaultTemplate = getDefaultTemplate(newType)
                if (!data.id && !template.id) {
                    setData((prev) => ({
                        ...prev,
                        type: newType,
                        name: defaultTemplate.name,
                        subject: defaultTemplate.subject,
                        templateVariables: defaultTemplate.templateVariables || {},
                    }))
                } else {
                    setData((prev) => ({ ...prev, type: newType }))
                }
            }
        },
        [data.type, data.id, template.id],
    )

    const handleTest = useCallback(async () => {
        if (!onTest || !testEmail) return
        setIsTesting(true)
        try {
            await onTest(testEmail)
            setIsTestDialogOpen(false)
        } catch (error) {
            console.error('Failed to send test email:', error)
        } finally {
            setIsTesting(false)
        }
    }, [onTest, testEmail])

    const handleLocaleChange = useCallback(
        (locale: LocaleCode) => {
            setCurrentLocale(locale)
            if (!data.subject?.[locale]) {
                setData((prev) => ({
                    ...prev,
                    subject: { ...prev.subject, [locale]: '' },
                }))
            }
        },
        [data.subject],
    )

    const availableVariables = getAvailableVariables((data.type as TemplateType) || 'abandoned_checkout')
    const subjectVariables = availableVariables.filter((v) => !v.key.includes('.'))
    const specificParams = data.type ? (templateRegistry[data.type as TemplateType]?.specificParams ?? {}) : {}

    const updateTemplateVariables = useCallback(
        (
            prev: Partial<EmailTemplateItem>,
            templateType: TemplateType | undefined,
            locale: string,
            field: 'body',
            value: string,
        ) => {
            if (!templateType) return prev
            return {
                ...prev,
                templateVariables: {
                    ...prev.templateVariables,
                    [templateType]: {
                        ...(prev.templateVariables ? prev.templateVariables[templateType] : undefined),
                        [field]: {
                            ...(prev.templateVariables?.[templateType]?.[field] || {}),
                            [locale]: value,
                        },
                    },
                },
            }
        },
        [],
    )

    const insertVariable = useCallback(
        (field: 'subject' | 'body') => (variable: string) => {
            const ref = field === 'subject' ? subjectRef : bodyRef
            if (!ref.current || !data.type) return

            const textarea = ref.current
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            const text = textarea.value
            const before = text.substring(0, start)
            const after = text.substring(end)
            const newText = `${before}{{ ${variable} }}${after}`

            if (field === 'subject') {
                setData((prev) => ({
                    ...prev,
                    subject: { ...prev.subject, [currentLocale]: newText },
                }))
            } else {
                setData((prev) => updateTemplateVariables(prev, data.type as TemplateType, currentLocale, 'body', newText))
            }

            setTimeout(() => {
                textarea.focus()
                const cursorPos = start + variable.length + 6
                textarea.setSelectionRange(cursorPos, cursorPos)
            }, 0)
        },
        [currentLocale, data.type, updateTemplateVariables],
    )

    const insertHtmlTag = useCallback(
        (tag: AVAILABLE_TAG) => {
            if (!bodyRef.current || !data.type) return

            const textarea = bodyRef.current
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            const selectedText = textarea.value.substring(start, end)
            const before = textarea.value.substring(0, start)
            const after = textarea.value.substring(end)
            const newText = `${before}<${tag}>${selectedText}</${tag}>${after}`

            setData((prev) => updateTemplateVariables(prev, data.type as TemplateType, currentLocale, 'body', newText))

            setTimeout(() => {
                textarea.focus()
                if (selectedText) {
                    textarea.setSelectionRange(start, end + 2 * tag.length + 5)
                } else {
                    textarea.setSelectionRange(start + tag.length + 2, start + tag.length + 2)
                }
            }, 0)
        },
        [currentLocale, data.type, updateTemplateVariables],
    )

    const getBodyContent = useCallback(
        (d: Partial<EmailTemplateItem>, locale: string): string => {
            if (!d.type || !d.templateVariables) return ''
            const typeVariables = d.templateVariables[d.type]
            if (!typeVariables?.body) return ''
            const value = typeVariables.body[locale]
            return typeof value === 'string' ? value : ''
        },
        [],
    )

    /* ------------------------------------------------------------------
       New visual email builder integration
    ------------------------------------------------------------------ */

    const getBodyJson = useCallback(
        (d: Partial<EmailTemplateItem>, locale: string): EmailDocument | null => {
            if (!d.type || !d.templateVariables) return null
            const typeVariables = d.templateVariables[d.type] as
                | { bodyJson?: Record<string, EmailDocument> }
                | undefined
            return typeVariables?.bodyJson?.[locale] ?? null
        },
        [],
    )

    const builderVariables = useMemo<EmailVariable[]>(
        () =>
            (availableVariables as Array<{ key: string; label?: string; description?: string; example?: string }>).map((v) => ({
                key: v.key,
                label: v.label,
                description: v.description,
                example: v.example,
            })),
        [availableVariables],
    )

    const starterTemplates: EmailStarterTemplate[] = useMemo(() => {
        // When no type has been chosen yet, show the generic gallery so the
        // merchant can still explore layouts. Once a type is chosen, we only
        // show starters that actually match — no more promo-code layouts
        // popping up inside a refund template.
        if (!data.type) return DEFAULT_STARTERS
        return STARTERS_BY_EMAIL_TYPE[data.type as string] ?? []
    }, [data.type])

    // State: legacy HTML editor toggle (off by default — we always prefer the
    // new visual builder). Surfaced as a small "advanced" switch near the
    // body card header.
    const [htmlModeOpen, setHtmlModeOpen] = useState(false)
    const [starterGalleryOpen, setStarterGalleryOpen] = useState(false)

    const hasLegacyHtml = !!getBodyContent(data, currentLocale) &&
        !getBodyJson(data, currentLocale)

    const handleBuilderChange = useCallback(
        (doc: EmailDocument) => {
            setData((prev) => {
                if (!prev.type) return prev
                const templateType = prev.type as TemplateType
                const typeVars =
                    (prev.templateVariables?.[templateType] as
                        | {
                              body?: Record<string, string>
                              bodyJson?: Record<string, EmailDocument>
                          }
                        | undefined) || {}
                const html = renderEmailDocument(doc)
                return {
                    ...prev,
                    templateVariables: {
                        ...prev.templateVariables,
                        [templateType]: {
                            ...typeVars,
                            body: { ...(typeVars.body || {}), [currentLocale]: html },
                            bodyJson: {
                                ...(typeVars.bodyJson || {}),
                                [currentLocale]: doc,
                            },
                        },
                    },
                } as typeof prev
            })
        },
        [currentLocale],
    )

    const handleStarterPick = useCallback(
        (template: EmailStarterTemplate) => {
            setData((prev) => {
                if (!prev.type) return prev
                const templateType = prev.type as TemplateType
                const typeVars =
                    (prev.templateVariables?.[templateType] as
                        | {
                              body?: Record<string, string>
                              bodyJson?: Record<string, EmailDocument>
                          }
                        | undefined) || {}
                const html = renderEmailDocument(template.doc)
                const nextSubject =
                    !prev.subject?.[currentLocale] && template.defaultSubject
                        ? {
                              ...prev.subject,
                              [currentLocale]: template.defaultSubject,
                          }
                        : prev.subject
                return {
                    ...prev,
                    subject: nextSubject,
                    templateVariables: {
                        ...prev.templateVariables,
                        [templateType]: {
                            ...typeVars,
                            body: { ...(typeVars.body || {}), [currentLocale]: html },
                            bodyJson: {
                                ...(typeVars.bodyJson || {}),
                                [currentLocale]: template.doc,
                            },
                        },
                    },
                } as typeof prev
            })
        },
        [currentLocale],
    )

    /* ------------------------------------------------------------------
       Live preview — render the current draft locally with mock variable
       values so merchants see results as they type, even before saving.
    ------------------------------------------------------------------ */
    const mockVariableValues = useMemo(
        () => buildMockVariableValues(builderVariables),
        [builderVariables],
    )

    const livePreviewHtml = useMemo(() => {
        const doc = getBodyJson(data, currentLocale)
        if (doc) {
            return renderEmailDocument(doc, { variableValues: mockVariableValues })
        }
        // Legacy HTML path — substitute {{ var }} against mock values
        const html = getBodyContent(data, currentLocale)
        if (!html) return ''
        return html.replace(
            /\{\{\s*([a-zA-Z0-9_.]+)(?:,\s*fallback=([^}]+))?\s*\}\}/g,
            (_full, key: string, fb?: string) => {
                const val = mockVariableValues[key]
                if (val !== undefined) return val
                return fb ?? `{{ ${key} }}`
            },
        )
    }, [data, currentLocale, mockVariableValues, getBodyJson, getBodyContent])

    const livePreviewSubject = useMemo(() => {
        const raw = data.subject?.[currentLocale] || ''
        return raw.replace(
            /\{\{\s*([a-zA-Z0-9_.]+)(?:,\s*fallback=([^}]+))?\s*\}\}/g,
            (_full, key: string, fb?: string) => mockVariableValues[key] ?? fb ?? key,
        )
    }, [data.subject, currentLocale, mockVariableValues])

    const hasBody =
        !!getBodyJson(data, currentLocale) ||
        !!getBodyContent(data, currentLocale)

    /* ------------------------------------------------------------------
       Compute per-locale completion status + the set of locales visible in
       the language switcher. The visible set is the union of:
         1. The curated default set (en/fr/es/de/it/pt)
         2. Locales that already have saved content for this template
         3. Locales the merchant activated in the current session
       This way merchants never lose sight of a language they started
       translating, and freshly-added languages appear immediately.
    ------------------------------------------------------------------ */
    const localeStatus = useMemo<Partial<Record<LocaleCode, LocaleStatus>>>(() => {
        const result: Partial<Record<LocaleCode, LocaleStatus>> = {}
        const allLocales = Object.keys(ALL_LOCALES) as LocaleCode[]
        for (const code of allLocales) {
            const subject = (data.subject?.[code] || '').trim()
            const hasSubject = subject.length > 0
            const json = getBodyJson(data, code)
            const html = getBodyContent(data, code)
            const hasBodyForLocale = !!json || (html || '').trim().length > 0
            if (hasSubject && hasBodyForLocale) result[code] = 'full'
            else if (hasSubject || hasBodyForLocale) result[code] = 'partial'
            else result[code] = 'empty'
        }
        return result
    }, [data, getBodyJson, getBodyContent])

    const visibleLocales = useMemo<LocaleCode[]>(() => {
        const set = new Set<LocaleCode>(DEFAULT_VISIBLE_LOCALES)
        // Surface any locale that already has content (subject or body).
        for (const [code, status] of Object.entries(localeStatus)) {
            if (status && status !== 'empty' && ALL_LOCALES[code]) {
                set.add(code as LocaleCode)
            }
        }
        for (const code of extraLocales) set.add(code)
        return Array.from(set)
    }, [localeStatus, extraLocales])

    const handleAddLocale = useCallback((code: LocaleCode) => {
        setExtraLocales((prev) => (prev.includes(code) ? prev : [...prev, code]))
    }, [])

    const [mobileView, setMobileView] = useState<'edit' | 'preview'>('edit')

    return (
        <ImagePickerProvider>
        <div className="flex h-screen flex-col bg-muted/20">
            {/* Sticky header */}
            <header className="z-20 shrink-0 border-b bg-background/95 px-3 py-2 backdrop-blur sm:px-5 supports-[backdrop-filter]:bg-background/75">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                        <Link to={backTo}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        </Link>
                        <div className="min-w-0">
                            <h1 className="truncate text-base font-semibold leading-tight">
                                {title}
                            </h1>
                            {subtitle && (
                                <p className="truncate text-[11px] text-muted-foreground">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Global locale selector */}
                        <LocaleSwitcher
                            currentLocale={currentLocale}
                            onLocaleChange={handleLocaleChange}
                            onAddLocale={handleAddLocale}
                            statusByLocale={localeStatus}
                            availableLocales={visibleLocales}
                            variant="solid"
                            align="end"
                        />

                        <Separator orientation="vertical" className="h-6" />

                        {/* Template settings popover */}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2.5"
                                >
                                    <Settings2 className="h-4 w-4 sm:mr-1.5" />
                                    <span className="hidden sm:inline">{t('editor.settings.button')}</span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="end"
                                sideOffset={8}
                                className="w-[380px] p-0"
                            >
                                <div className="border-b bg-muted/30 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                        <h3 className="text-sm font-semibold">{t('editor.settings.title')}</h3>
                                    </div>
                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                        {t('editor.settings.subtitle')}
                                    </p>
                                </div>
                                <div className="space-y-4 p-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="name" className="text-xs">
                                            {t('fields.templateName')}
                                        </Label>
                                        <Input
                                            id="name"
                                            value={data.name || ''}
                                            onChange={(e) =>
                                                setData((prev) => ({ ...prev, name: e.target.value }))
                                            }
                                            placeholder={t('placeholders.templateName')}
                                            disabled={isLoading}
                                            className="h-8"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="type" className="text-xs">
                                            {t('fields.templateType')}
                                        </Label>
                                        <Select
                                            value={data.type}
                                            onValueChange={(value) =>
                                                handleTypeChange(value as TemplateType)
                                            }
                                            disabled={!!template.id || isLoading}
                                        >
                                            <SelectTrigger className="h-8">
                                                <SelectValue placeholder={t('editor.settings.selectEventPlaceholder')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {EMAIL_TYPES.map((type) => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="replyTo" className="text-xs">
                                            {t('fields.replyToEmail')}
                                        </Label>
                                        <Input
                                            id="replyTo"
                                            type="email"
                                            value={data.params?.replyTo || ''}
                                            onChange={(e) =>
                                                setData((prev) => ({
                                                    ...prev,
                                                    params: {
                                                        ...prev.params,
                                                        replyTo: e.target.value,
                                                    },
                                                }))
                                            }
                                            placeholder={t('placeholders.replyToEmail')}
                                            className="h-8"
                                        />
                                        <p className="text-[11px] text-muted-foreground">
                                            {t('hints.replyToEmail')}
                                        </p>
                                    </div>

                                    {Object.keys(specificParams).length > 0 && (
                                        <>
                                            <Separator />
                                            {Object.entries(specificParams).map(([key, param]) => {
                                                const typedParam = param as {
                                                    type: string
                                                    label: string
                                                    default: string
                                                    options: { value: string; label: string }[]
                                                }
                                                if (typedParam.type !== 'select') return null
                                                return (
                                                    <div key={key} className="space-y-1.5">
                                                        <Label className="text-xs">
                                                            {typedParam.label}
                                                        </Label>
                                                        <Select
                                                            value={
                                                                data.params?.specific?.[key] ||
                                                                typedParam.default
                                                            }
                                                            onValueChange={(value) =>
                                                                setData((prev) => ({
                                                                    ...prev,
                                                                    params: {
                                                                        ...prev.params,
                                                                        specific: {
                                                                            ...prev.params?.specific,
                                                                            [key]: value,
                                                                        },
                                                                    },
                                                                }))
                                                            }
                                                        >
                                                            <SelectTrigger className="h-8">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {typedParam.options.map((option) => (
                                                                    <SelectItem
                                                                        key={option.value}
                                                                        value={option.value}
                                                                    >
                                                                        {option.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                )
                                            })}
                                        </>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>

                        {/* Active toggle */}
                        <div className="flex items-center gap-1.5">
                            <Switch
                                checked={data.active}
                                onCheckedChange={(checked) =>
                                    setData((prev) => ({ ...prev, active: checked }))
                                }
                                disabled={isLoading}
                            />
                            <Label className="hidden text-xs font-normal text-muted-foreground sm:block">
                                {data.active
                                    ? t('fields.active')
                                    : t('fields.inactive')}
                            </Label>
                        </div>

                        <Separator orientation="vertical" className="h-6" />

                        <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isLoading || !template.id}
                                >
                                    <Beaker className="h-3.5 w-3.5 sm:mr-1.5" />
                                    <span className="hidden sm:inline">
                                        {t('actions.testTemplate')}
                                    </span>
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>
                                        {t('dialog.sendTestEmail')}
                                    </DialogTitle>
                                    <DialogDescription>
                                        {t('editor.test.description')}
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="testEmail">{t('editor.test.recipient')}</Label>
                                        <Input
                                            id="testEmail"
                                            type="email"
                                            value={testEmail}
                                            onChange={(e) => setTestEmail(e.target.value)}
                                            placeholder="you@example.com"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground">{t('editor.test.localeLabel')}</span>
                                        <LocaleSwitcher
                                            currentLocale={currentLocale}
                                            onLocaleChange={setCurrentLocale}
                                            onAddLocale={handleAddLocale}
                                            statusByLocale={localeStatus}
                                            availableLocales={visibleLocales}
                                            variant="solid"
                                            align="start"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={handleTest}
                                        disabled={!testEmail || isTesting}
                                        className="w-full"
                                    >
                                        {isTesting
                                            ? t('actions.sending')
                                            : t('actions.sendTestEmail')}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Button size="sm" onClick={() => handleSubmit()} disabled={isLoading}>
                            <Save className="h-3.5 w-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline">
                                {t('actions.saveTemplate')}
                            </span>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Subject bar */}
            <div className="shrink-0 border-b bg-background px-3 py-2 sm:px-5">
                <div className="flex items-center gap-2">
                    <Label className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('fields.subject')}
                    </Label>
                    <Input
                        ref={subjectRef as unknown as React.Ref<HTMLInputElement>}
                        value={data.subject?.[currentLocale] || ''}
                        onChange={(e) =>
                            setData((prev) => ({
                                ...prev,
                                subject: {
                                    ...prev.subject,
                                    [currentLocale]: e.target.value,
                                },
                            }))
                        }
                        placeholder={t('placeholders.emailSubject')}
                        disabled={isLoading}
                        className="h-8 flex-1 border-transparent bg-transparent px-2 text-sm font-medium shadow-none focus-visible:border-input focus-visible:bg-background"
                    />
                    {subjectVariables.length > 0 && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                                >
                                    <Sparkles className="h-3 w-3" />
                                    {t('editor.subject.variableButton')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-64 p-2">
                                <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    {t('editor.subject.insertVariable')}
                                </p>
                                <div className="flex flex-col gap-0.5">
                                    {subjectVariables.map((variable) => (
                                        <button
                                            key={variable.key}
                                            type="button"
                                            onClick={() => insertVariable('subject')(variable.key)}
                                            className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-muted"
                                        >
                                            <span className="font-medium">
                                                {variable.label || variable.key}
                                            </span>
                                            <span className="font-mono text-[10px] text-muted-foreground">
                                                {'{{' + variable.key + '}}'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                    {/* Mobile edit/preview tabs */}
                    <div className="flex shrink-0 rounded-md border bg-muted/40 p-0.5 lg:hidden">
                        <button
                            type="button"
                            onClick={() => setMobileView('edit')}
                            className={cn(
                                'flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-medium transition-colors',
                                mobileView === 'edit'
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground',
                            )}
                        >
                            <Pencil className="h-3 w-3" />
                            {t('editor.mobileTabs.edit')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileView('preview')}
                            className={cn(
                                'flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-medium transition-colors',
                                mobileView === 'preview'
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground',
                            )}
                        >
                            <Eye className="h-3 w-3" />
                            {t('editor.mobileTabs.preview')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Main split: editor | live preview */}
            <main className="flex min-h-0 flex-1 flex-col lg:flex-row">
                {/* Editor column */}
                <section
                    className={cn(
                        'flex min-h-0 flex-1 flex-col bg-background',
                        mobileView !== 'edit' && 'hidden lg:flex',
                    )}
                >
                    {/* Editor toolbar */}
                    <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-1.5">
                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Pencil className="h-3 w-3" />
                            <span className="font-medium">{t('editor.toolbar.content')}</span>
                            <span className="hidden text-muted-foreground/60 md:inline">
                                · <kbd className="rounded border bg-muted px-1">/</kbd> {t('editor.toolbar.blocks')}
                                · <kbd className="rounded border bg-muted px-1">@</kbd> {t('editor.toolbar.variables')}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => setStarterGalleryOpen(true)}
                                        disabled={!data.type || isLoading}
                                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                                    >
                                        <Sparkles className="h-3 w-3" />
                                        {t('editor.toolbar.templates')}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    {t('editor.toolbar.templatesTooltip')}
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => setHtmlModeOpen((v) => !v)}
                                        className={cn(
                                            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition-colors',
                                            htmlModeOpen
                                                ? 'bg-primary/10 text-primary'
                                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                        )}
                                    >
                                        <Code2 className="h-3 w-3" />
                                        {t('editor.toolbar.html')}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    {t('editor.toolbar.htmlTooltip')}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Legacy HTML banner */}
                    {hasLegacyHtml && !htmlModeOpen && (
                        <div className="shrink-0 border-b bg-amber-50 px-4 py-2">
                            <div className="flex items-start gap-2 text-xs text-amber-800">
                                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <div className="flex-1">
                                    <div className="font-medium">{t('editor.legacyBanner.title')}</div>
                                    <div className="mt-0.5 text-amber-800/80">
                                        {t('editor.legacyBanner.hint')}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setStarterGalleryOpen(true)}
                                    className="shrink-0 rounded-md bg-amber-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-700"
                                >
                                    {t('editor.legacyBanner.action')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Editor body */}
                    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
                        {!hasBody && !htmlModeOpen ? (
                            <EmptyState
                                currentType={data.type as TemplateType | undefined}
                                onPickType={handleTypeChange}
                                starters={starterTemplates.slice(0, 3)}
                                disabled={!!isLoading}
                                mockValues={mockVariableValues}
                                onPick={handleStarterPick}
                                onBrowseAll={() => setStarterGalleryOpen(true)}
                                onStartBlank={() =>
                                    handleBuilderChange({
                                        type: 'doc',
                                        content: [{ type: 'paragraph' }],
                                    })
                                }
                            />
                        ) : htmlModeOpen ? (
                            <div className="flex flex-1 flex-col gap-2 p-4">
                                <div className="flex items-center gap-0.5 self-start rounded-md border bg-muted/30 p-0.5">
                                    {TAG_CONFIG.map(({ tag, icon: Icon, label }) => (
                                        <Tooltip key={tag}>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={() => insertHtmlTag(tag)}
                                                    className="rounded-sm p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                                                >
                                                    <Icon className="h-3.5 w-3.5" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">{t(`htmlTags.${label.toLowerCase()}`)}</TooltipContent>
                                        </Tooltip>
                                    ))}
                                </div>
                                <Textarea
                                    ref={bodyRef}
                                    value={getBodyContent(data, currentLocale)}
                                    onChange={(e) =>
                                        setData((prev) => {
                                            if (!prev.type) return prev
                                            return updateTemplateVariables(
                                                prev,
                                                prev.type as TemplateType,
                                                currentLocale,
                                                'body',
                                                e.target.value,
                                            )
                                        })
                                    }
                                    placeholder={t('placeholders.emailBody')}
                                    disabled={isLoading}
                                    className="flex-1 resize-none font-mono text-sm"
                                />
                                {availableVariables.length > 0 && (
                                    <div>
                                        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                            {t('editor.availableVariables')}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {availableVariables.map((variable) => (
                                                <VariableChip
                                                    key={variable.key}
                                                    variable={variable}
                                                    onClick={() =>
                                                        insertVariable('body')(variable.key)
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mx-auto w-full max-w-[760px] flex-1 p-3 sm:p-5">
                                <EmailBuilder
                                    value={getBodyJson(data, currentLocale)}
                                    onChange={handleBuilderChange}
                                    variables={builderVariables}
                                    readOnly={isLoading}
                                    className="min-h-full"
                                />
                            </div>
                        )}
                    </div>
                </section>

                {/* Live preview column */}
                <section
                    className={cn(
                        'flex min-h-0 shrink-0 flex-col border-t bg-muted/40 lg:w-[46%] lg:border-l lg:border-t-0 xl:w-[44%] 2xl:w-[40%]',
                        mobileView !== 'preview' && 'hidden lg:flex',
                    )}
                >
                    <div className="flex shrink-0 items-center justify-between gap-2 border-b bg-background/60 px-3 py-1.5">
                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Eye className="h-3 w-3" />
                            <span className="font-medium">{t('editor.preview.title')}</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Monitor className="h-3 w-3" />
                            <span>{t('editor.preview.desktop')}</span>
                            <span className="opacity-40">·</span>
                            <Smartphone className="h-3 w-3" />
                            <span>{t('editor.preview.mobileFriendly')}</span>
                        </div>
                    </div>
                    <div className="min-h-0 flex-1">
                        <PreviewFrame
                            html={livePreviewHtml}
                            subject={livePreviewSubject}
                            fromName={data.name || 'Your store'}
                            fromEmail={data.params?.replyTo || 'noreply@yourstore.com'}
                            pageBg="#f3f4f6"
                        />
                    </div>
                </section>
            </main>

            <StarterGallery
                open={starterGalleryOpen}
                onOpenChange={setStarterGalleryOpen}
                templates={starterTemplates}
                variables={builderVariables}
                onSelect={handleStarterPick}
            />
        </div>
        </ImagePickerProvider>
    )
}

/* ----------------------------------------------------------------------------
   Empty-state shown when the body is blank. Beginner-friendly landing that
   nudges merchants toward starter templates (our highest-leverage UX).
---------------------------------------------------------------------------- */

function EmptyState(props: {
    currentType: TemplateType | undefined
    onPickType: (type: TemplateType) => void
    starters: EmailStarterTemplate[]
    disabled: boolean
    mockValues: Record<string, string>
    onPick: (template: EmailStarterTemplate) => void
    onBrowseAll: () => void
    onStartBlank: () => void
}) {
    const { t } = useTranslation(['emails'])
    const {
        currentType,
        onPickType,
        starters,
        disabled,
        mockValues,
        onPick,
        onBrowseAll,
        onStartBlank,
    } = props

    // Step 1 of the onboarding: pick the trigger. This lives front-and-center
    // (no longer hidden inside the settings popover) so merchants never get
    // stuck wondering "what is this email for?".
    if (!currentType) {
        return <EmailTypePicker onPick={onPickType} disabled={disabled} />
    }

    const currentMeta = EMAIL_TYPE_META[currentType]
    const currentLabel = EMAIL_TYPES.find((t) => t.value === currentType)?.label ?? currentType

    return (
        <div className="flex flex-1 items-center justify-center p-6">
            <div className="w-full max-w-3xl">
                <div className="mb-5 flex flex-col items-center text-center">
                    <div
                        className="mb-3 flex h-12 w-12 items-center justify-center rounded-full"
                        style={{
                            backgroundColor: `${currentMeta?.accent ?? '#6366f1'}1A`,
                            color: currentMeta?.accent ?? '#6366f1',
                        }}
                    >
                        {currentMeta?.icon ? (
                            <currentMeta.icon className="h-5 w-5" />
                        ) : (
                            <Sparkles className="h-5 w-5" />
                        )}
                    </div>
                    <h2 className="text-lg font-semibold">{t('editor.empty.title')}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        <Trans
                            i18nKey="editor.empty.subtitle"
                            ns="emails"
                            values={{ label: currentLabel }}
                            components={{ label: <span className="font-medium text-foreground" /> }}
                        />
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {starters.map((tpl) => {
                        const html = renderEmailDocument(tpl.doc, {
                            variableValues: mockValues,
                        })
                        return (
                            <button
                                key={tpl.id}
                                type="button"
                                onClick={() => !disabled && onPick(tpl)}
                                disabled={disabled}
                                className={cn(
                                    'group flex flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm',
                                    'transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md',
                                    'disabled:pointer-events-none disabled:opacity-50',
                                )}
                            >
                                <div className="relative aspect-[4/5] overflow-hidden border-b bg-muted/30">
                                    <PreviewFrame
                                        html={html}
                                        subject={tpl.defaultSubject}
                                        fromName="Your store"
                                        fromEmail="noreply@yourstore.com"
                                        scale={0.45}
                                        pageBg="#f9fafb"
                                    />
                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent" />
                                </div>
                                <div className="p-3">
                                    <div className="truncate text-sm font-semibold">
                                        {tpl.label}
                                    </div>
                                    <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                                        {tpl.description}
                                    </div>
                                </div>
                            </button>
                        )
                    })}
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onBrowseAll}
                        disabled={disabled}
                    >
                        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                        {t('editor.empty.browseAll')}
                    </Button>
                    <span className="text-xs text-muted-foreground">{t('editor.empty.or')}</span>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onStartBlank}
                        disabled={disabled}
                    >
                        {t('editor.empty.startBlank')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

/* ----------------------------------------------------------------------------
   Visual picker for the email trigger. Shown as the first thing a merchant
   sees when creating a template — much more discoverable than a plain Select
   tucked inside a settings popover.
---------------------------------------------------------------------------- */

function EmailTypePicker({
    onPick,
    disabled,
}: {
    onPick: (type: TemplateType) => void
    disabled: boolean
}) {
    const { t } = useTranslation(['emails'])
    // Group types so the list is scannable. Within each group, preserve the
    // original registry ordering (acts as an implicit priority list).
    const grouped = useMemo(() => {
        const acc: Record<string, Array<{ value: string; label: string; description: string }>> = {}
        for (const t of EMAIL_TYPES) {
            const meta = EMAIL_TYPE_META[t.value]
            const group = meta?.group ?? 'Other'
            acc[group] = acc[group] ?? []
            acc[group].push(t)
        }
        return acc
    }, [])

    const orderedGroups = [
        ...GROUP_ORDER.filter((g) => grouped[g]),
        ...Object.keys(grouped).filter((g) => !GROUP_ORDER.includes(g)),
    ]

    return (
        <div className="flex flex-1 justify-center overflow-auto p-6">
            <div className="w-full max-w-4xl">
                <div className="mb-6 flex flex-col items-center text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500/15 to-violet-500/15 text-indigo-600">
                        <Mail className="h-5 w-5" />
                    </div>
                    <h2 className="text-lg font-semibold">{t('editor.typePicker.title')}</h2>
                    <p className="mt-1 max-w-md text-sm text-muted-foreground">
                        {t('editor.typePicker.subtitle')}
                    </p>
                </div>

                <div className="space-y-5">
                    {orderedGroups.map((group) => (
                        <div key={group}>
                            <div className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {t(`editor.typePicker.groups.${group.toLowerCase()}`, { defaultValue: group })}
                            </div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {grouped[group].map((t) => {
                                    const meta = EMAIL_TYPE_META[t.value]
                                    const Icon = meta?.icon ?? Mail
                                    const accent = meta?.accent ?? '#6366f1'
                                    return (
                                        <button
                                            key={t.value}
                                            type="button"
                                            onClick={() => !disabled && onPick(t.value as TemplateType)}
                                            disabled={disabled}
                                            className={cn(
                                                'group flex items-start gap-3 rounded-xl border bg-card p-3 text-left',
                                                'transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md',
                                                'disabled:pointer-events-none disabled:opacity-50',
                                            )}
                                        >
                                            <div
                                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                                                style={{
                                                    backgroundColor: `${accent}1A`,
                                                    color: accent,
                                                }}
                                            >
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1 text-sm font-semibold">
                                                    <span className="truncate">{t.label}</span>
                                                </div>
                                                {t.description && (
                                                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                                        {t.description}
                                                    </div>
                                                )}
                                            </div>
                                            <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
